/**
 * FRONT-2387: turns speakable text into audio playing out of the browser's
 * speakers, via `POST /api/voice/speak` (the same TTS engine
 * `use-tour-narration.ts` and `use-voice-sample-player.ts` already call) and
 * one shared Web Audio `AudioContext` — never `<audio>`/`HTMLAudioElement`,
 * because pipelined chunk-by-chunk playback needs `decodeAudioData` and
 * precise back-to-back scheduling that a plain `<audio src>` doesn't offer.
 *
 * FRONT-2387 (change round 3): audio only ever starts from a click/tap on
 * the play control itself — there is no autoplay and nothing to prime ahead
 * of time. `primeAudio()` is still called synchronously at the top of that
 * SAME click handler, before any `await` — iOS Safari only allows an
 * `AudioContext` to start/resume from inside a real user gesture's call
 * stack, and the click is that gesture.
 */
import { fetchSpeechBytes } from './audio-answer-fetch';
import { createChunkPrefetcher } from './audio-answer-prefetch';
import { chunkForSpeech } from './audio-answer-text';

export type AudioAnswerPlaybackState = 'loading' | 'playing' | 'done' | 'stopped' | 'error';

interface SpeakOptions {
  onState?: (state: AudioAnswerPlaybackState) => void;
  /** Voice id — omitted lets the server fall back to its own default,
   *  matching every other caller of this endpoint. */
  voice?: string;
}

export interface SpeakHandle {
  stop: () => void;
}

interface TtsModelsResponse {
  models?: { id: string }[];
}

/** The model measured to deliver a COMPLETE clip fastest — which is what this
 *  player waits for (`fetchSpeechBytes` collects the whole body, then
 *  `decodeAudioData`), so first-byte latency is irrelevant here. Measured
 *  through the local `/api/voice/speak` for one 400-char text: tts-1 1.7-2.0 s
 *  (1000 chars 3.2 s, 12 chars 1.1-1.6 s); gpt-4o-mini-tts 8.7 s, 9.5 s,
 *  25.4 s and twice the 60 s server timeout with only 3-4 s of audio
 *  delivered (12 chars once took 18.4 s). Used only when the tenant's TTS
 *  backend actually lists it; otherwise the request omits `model` entirely
 *  and the server applies its own configured default. */
const PREFERRED_MODEL = 'tts-1';

/** The playing chunk plus this many requested ahead. Bounded so a long
 *  answer's decoded audio never piles up in phone memory (at most
 *  LOOKAHEAD + 1 buffers alive), yet deep enough that a slow clip is
 *  already in flight while the previous ones play (FRONT-3103). */
const LOOKAHEAD = 2;

let sharedContext: AudioContext | null = null;

function getContext(): AudioContext {
  if (!sharedContext) sharedContext = new AudioContext();
  return sharedContext;
}

/** Create/resume the shared context. See the module doc for the user-gesture
 *  requirement — call this FIRST, before anything else in the same handler.
 *
 * FRONT-2387 fix round 1 (#8): resumes on ANY non-`running` state, not only
 * `suspended` — Safari can leave a context `interrupted` (a phone call, the
 * ringer switch, another app grabbing audio focus) and `resume()` is what
 * clears that too. The promise is deliberately given its own `.catch` — a
 * bare `void ctx.resume()` discards the return value but does NOT stop a
 * later rejection from surfacing as an unhandled-rejection warning; `speak()`
 * below is what actually reacts to a resume that doesn't take (ending in
 * `error` rather than silently doing nothing); the rejection is still logged
 * so a context that refuses to resume leaves a diagnostic trail. */
export function primeAudio(): void {
  const ctx = getContext();
  if (ctx.state !== 'running') {
    ctx.resume().catch((err: unknown) => {
      console.warn('[audio-answer-player] audio context did not resume:', err);
    });
  }
}

// Fetched once per page load and cached — every `speak()` call reuses it.
let ttsModelPromise: Promise<string | undefined> | null = null;

async function resolvePreferredModel(): Promise<string | undefined> {
  if (!ttsModelPromise) {
    ttsModelPromise = (async () => {
      try {
        const res = await fetch('/api/voice/tts-models');
        if (!res.ok) return undefined;
        const data = (await res.json()) as TtsModelsResponse;
        return data.models?.some((m) => m.id === PREFERRED_MODEL) ? PREFERRED_MODEL : undefined;
      } catch {
        return undefined;
      }
    })();
  }
  return ttsModelPromise;
}

async function fetchChunkAudio(
  ctx: AudioContext,
  text: string,
  voice?: string,
  signal?: AbortSignal,
): Promise<AudioBuffer> {
  const model = await resolvePreferredModel();
  const body: { text: string; model?: string; voice?: string } = { text };
  if (model) body.model = model;
  if (voice) body.voice = voice;
  // `signal` lets `speak()`'s `stop()` cancel the underlying request (and
  // skip a pointless decode); the stall guard + single retry live in
  // `fetchSpeechBytes` (FRONT-3103).
  const arrayBuffer = await fetchSpeechBytes(body, { signal });
  return ctx.decodeAudioData(arrayBuffer);
}

/** FRONT-2387 fix round 2 (#4): `ctx.resume()` has no documented upper bound
 *  — a still-blocked/interrupted context can leave it pending indefinitely,
 *  which without a bound left `speak()` stuck in `loading` forever (a
 *  spinner with no way back to "Play answer"). */
const RESUME_TIMEOUT_MS = 1500;

/**
 * Resolve once the shared context is genuinely `running`, attempting a
 * resume first if it isn't. Returns whether it ended up running — `false`
 * means the context could not be started at all (a still-active OS-level
 * interruption, a blocked autoplay policy `primeAudio()`'s own earlier call
 * in the same gesture didn't clear, or `resume()` simply never settling
 * within `RESUME_TIMEOUT_MS`), which `speak()` below treats as a hard
 * failure rather than silently scheduling audio that will never play.
 */
async function ensureContextRunning(ctx: AudioContext): Promise<boolean> {
  if (ctx.state === 'running') return true;
  const resumed = await Promise.race([
    ctx.resume().then(
      () => true,
      () => false,
    ),
    new Promise<boolean>((resolve) => setTimeout(() => resolve(false), RESUME_TIMEOUT_MS)),
  ]);
  if (!resumed) return false;
  return (ctx.state as AudioContextState) === 'running';
}

/** The one playback in flight app-wide, if any — `speak()` stops it before
 *  starting a new one, which is what makes "only one plays at a time" true
 *  regardless of how many callers exist. */
let activeHandle: SpeakHandle | null = null;

/** Stop whatever is currently playing/loading, if anything. Exported for
 *  callers that need to guarantee silence without holding onto the handle
 *  `speak()` returned — the play control's own Stop click, and the
 *  surface-unmount/session-change hook (`use-stop-audio-answer.ts`). */
export function stopActivePlayback(): void {
  activeHandle?.stop();
}

/**
 * Speak `text`: chunks it (see `audio-answer-text.ts`), requests the first
 * LOOKAHEAD + 1 chunks immediately and each further chunk as the one
 * LOOKAHEAD before it starts playing (`audio-answer-prefetch.ts`), decodes
 * each chunk, and plays the buffers back-to-back. `onState` is called with
 * `loading` before the first chunk is ready, `playing` once audio is
 * actually sounding, `done` when every chunk has finished, `stopped` if
 * `stop()` was called, and `error` on a fetch/decode failure (playback stops
 * quietly — no toast storm; the caller's UI falls back to showing "Play
 * answer" again).
 */
export function speak(text: string, options: SpeakOptions = {}): SpeakHandle {
  const { onState, voice } = options;
  activeHandle?.stop();

  let stopped = false;
  let currentSource: AudioBufferSourceNode | null = null;
  const ctx = getContext();
  // FRONT-2387 fix round 1 (#8): the one thing every in-flight TTS fetch
  // this handle owns can be cancelled through.
  const abortController = new AbortController();

  const handle: SpeakHandle = {
    stop: () => {
      if (stopped) return;
      stopped = true;
      abortController.abort();
      if (currentSource) {
        try {
          currentSource.stop();
        } catch {
          /* already stopped/ended */
        }
        currentSource = null;
      }
      if (activeHandle === handle) activeHandle = null;
      onState?.('stopped');
    },
  };
  activeHandle = handle;

  const chunks = chunkForSpeech(text);
  if (chunks.length === 0) {
    activeHandle = null;
    onState?.('done');
    return handle;
  }

  // FRONT-2387 fix round 1 (#8): `playing` now fires from IN HERE, right
  // after `source.start()` — never optimistically ahead of it — so a
  // caller's "Play answer" button only ever reads "playing" once a buffer
  // has genuinely been scheduled against a running context.
  function playBuffer(buffer: AudioBuffer): Promise<void> {
    return new Promise((resolve) => {
      if (stopped) {
        resolve();
        return;
      }
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      currentSource = source;
      source.onended = () => {
        if (currentSource === source) currentSource = null;
        resolve();
      };
      source.start();
      onState?.('playing');
    });
  }

  (async () => {
    try {
      onState?.('loading');
      // FRONT-2387 fix round 1 (#8): resume BEFORE fetching/decoding
      // anything. Safari can leave the shared context `interrupted` (not
      // only `suspended`) between turns — `primeAudio()`'s own resume in
      // the send/click gesture usually already covers this; this is the
      // backstop for whenever it didn't. If the context genuinely cannot
      // run (a still-active interruption, a blocked autoplay policy), end
      // in `error` here, before any fetch — never stuck showing "Stop" for
      // audio that was never going to play.
      if (!(await ensureContextRunning(ctx))) {
        if (!stopped) {
          onState?.('error');
          if (activeHandle === handle) activeHandle = null;
        }
        return;
      }
      if (stopped) return;

      // Chunks 0..LOOKAHEAD are requested at once; each later chunk is
      // requested as the one LOOKAHEAD behind it starts playing. A started
      // fetch's rejection is silenced inside the prefetcher until awaited.
      const prefetcher = createChunkPrefetcher(chunks.length, LOOKAHEAD, (i) =>
        fetchChunkAudio(ctx, chunks[i], voice, abortController.signal),
      );
      for (let i = 0; i < chunks.length; i++) {
        if (stopped) return;
        const buffer = await prefetcher.get(i);
        if (stopped || !buffer) return;
        await playBuffer(buffer);
        // Played: drop it so the decoded audio can be garbage-collected.
        prefetcher.release(i);
      }
      if (!stopped) {
        onState?.('done');
        if (activeHandle === handle) activeHandle = null;
      }
    } catch (err) {
      // A failed chunk must not leave the later, already-started chunk
      // requests downloading (and retrying, and billing) behind the error.
      // Aborting here does not touch `stopped`, so the reported state stays
      // `error`, never `stopped`.
      abortController.abort();
      if (!stopped) {
        console.warn('[audio-answer-player] speak failed');
        onState?.('error');
        if (activeHandle === handle) activeHandle = null;
      }
    }
  })();

  return handle;
}
